import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type {
  CustomFieldDataType,
  CustomFieldDefinition,
  CustomFieldEntityType,
} from '@neuroedge/shared';
import { Button, Card, PageState, Select, TextField, useToast } from '../../components/ui';
import { ApiClientError } from '../../api/client';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import { useAuth } from '../../auth/AuthContext';
import {
  useCustomFieldDefinitions,
  useCreateCustomField,
  useUpdateCustomField,
  useDeleteCustomField,
} from '../../api/custom-fields';

const TYPES: CustomFieldDataType[] = [
  'TEXT',
  'NUMBER',
  'BOOLEAN',
  'DATE',
  'DATETIME',
  'SELECT',
  'MULTI_SELECT',
  'PHONE',
];
const NEEDS_OPTIONS = (t: CustomFieldDataType) => t === 'SELECT' || t === 'MULTI_SELECT';

/** Mirrors the backend's `createCustomFieldBody`/`updateCustomFieldBody` limits
 * (custom-fields.schemas.ts): `label` 1-120 chars, each option 1-120 chars (max
 * 50 options), and SELECT/MULTI_SELECT types need at least one option. */
function validateCustomField(
  label: string,
  dataType: CustomFieldDataType,
  optionsCsv: string,
): Record<string, string> {
  const errors: Record<string, string> = {};
  const trimmedLabel = label.trim();
  if (trimmedLabel.length < 1) {
    errors.label = 'Enter a label.';
  } else if (trimmedLabel.length > 120) {
    errors.label = 'Label must be 120 characters or fewer.';
  }
  if (NEEDS_OPTIONS(dataType)) {
    const opts = optionsCsv
      .split(',')
      .map((o) => o.trim())
      .filter(Boolean);
    if (opts.length === 0) {
      errors.options = 'SELECT and MULTI_SELECT fields need at least one option.';
    } else if (opts.length > 50) {
      errors.options = 'A field may have at most 50 options.';
    } else if (opts.some((o) => o.length > 120)) {
      errors.options = 'Each option must be 120 characters or fewer.';
    }
  }
  return errors;
}

function DefRow({ def, canManage }: { def: CustomFieldDefinition; canManage: boolean }) {
  const update = useUpdateCustomField();
  const del = useDeleteCustomField();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(def.label);
  const [required, setRequired] = useState(def.required);
  const [isActive, setIsActive] = useState(def.isActive);
  const [options, setOptions] = useState(def.options.join(', '));
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  async function save() {
    const errors = validateCustomField(label, def.dataType, options);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError('Please fix the highlighted fields.');
      return;
    }
    setError(null);
    try {
      await update.mutateAsync({
        id: def.id,
        patch: {
          label: label.trim(),
          required,
          isActive,
          ...(NEEDS_OPTIONS(def.dataType)
            ? {
                options: options
                  .split(',')
                  .map((o) => o.trim())
                  .filter(Boolean),
              }
            : {}),
        },
      });
      setEditing(false);
      toast.success('Custom field updated.');
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const serverFieldErrors = fieldErrorsFromApiError(err);
        if (Object.keys(serverFieldErrors).length > 0) {
          setFieldErrors(serverFieldErrors);
          setError('Please fix the highlighted fields.');
          toast.error('Please fix the highlighted fields.');
          return;
        }
      }
      const message = err instanceof Error ? err.message : 'Could not save.';
      setError(message);
      toast.error(message);
    }
  }

  return (
    <tr style={{ opacity: def.isActive ? 1 : 0.55 }}>
      <td>
        {editing ? (
          <>
            <input
              className={['ne-input', fieldErrors.label ? 'ne-input--error' : '']
                .filter(Boolean)
                .join(' ')}
              value={label}
              aria-invalid={fieldErrors.label ? true : undefined}
              onChange={(e) => {
                setLabel(e.target.value);
                setFieldErrors((f) => {
                  if (!f.label) return f;
                  const { label: _removed, ...rest } = f;
                  return rest;
                });
              }}
            />
            {fieldErrors.label ? (
              <span className="ne-field__error" role="alert">
                {fieldErrors.label}
              </span>
            ) : null}
          </>
        ) : (
          def.label
        )}
      </td>
      <td>
        <code>{def.fieldKey}</code>
      </td>
      <td>{def.dataType}</td>
      <td style={{ textAlign: 'center' }}>
        {editing ? (
          <input
            type="checkbox"
            checked={required}
            onChange={(e) => setRequired(e.target.checked)}
          />
        ) : def.required ? (
          'Yes'
        ) : (
          '—'
        )}
      </td>
      <td>
        {NEEDS_OPTIONS(def.dataType) ? (
          editing ? (
            <>
              <input
                className={['ne-input', fieldErrors.options ? 'ne-input--error' : '']
                  .filter(Boolean)
                  .join(' ')}
                value={options}
                aria-invalid={fieldErrors.options ? true : undefined}
                onChange={(e) => {
                  setOptions(e.target.value);
                  setFieldErrors((f) => {
                    if (!f.options) return f;
                    const { options: _removed, ...rest } = f;
                    return rest;
                  });
                }}
              />
              {fieldErrors.options ? (
                <span className="ne-field__error" role="alert">
                  {fieldErrors.options}
                </span>
              ) : null}
            </>
          ) : (
            def.options.join(', ')
          )
        ) : (
          '—'
        )}
      </td>
      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        {!canManage ? null : editing ? (
          <>
            <label style={{ marginRight: 'var(--space-2)', fontSize: 'var(--font-size-sm)' }}>
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />{' '}
              active
            </label>
            <Button disabled={update.isPending} onClick={() => void save()}>
              Save
            </Button>{' '}
            <Button
              variant="secondary"
              onClick={() => {
                setLabel(def.label);
                setRequired(def.required);
                setIsActive(def.isActive);
                setOptions(def.options.join(', '));
                setError(null);
                setFieldErrors({});
                setEditing(false);
              }}
            >
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={() => setEditing(true)}>
              Edit
            </Button>{' '}
            <Button
              variant="secondary"
              disabled={del.isPending}
              onClick={() => {
                if (window.confirm(`Delete "${def.label}"? Stored values are kept but hidden.`)) {
                  del.mutate(def.id, {
                    onSuccess: () => toast.success('Custom field deleted.'),
                    onError: (err) =>
                      toast.error(
                        err instanceof Error ? err.message : 'Could not delete the field.',
                      ),
                  });
                }
              }}
            >
              Delete
            </Button>
          </>
        )}
        {error ? (
          <div style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)' }}>
            {error}
          </div>
        ) : null}
      </td>
    </tr>
  );
}

export function CustomFieldsPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('custom_fields.manage');
  const [entityType, setEntityType] = useState<CustomFieldEntityType>('CONTACT');
  const defs = useCustomFieldDefinitions(entityType, true);
  const create = useCreateCustomField();
  const toast = useToast();

  const [label, setLabel] = useState('');
  const [dataType, setDataType] = useState<CustomFieldDataType>('TEXT');
  const [options, setOptions] = useState('');
  const [required, setRequired] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    const errors = validateCustomField(label, dataType, options);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError('Please fix the highlighted fields.');
      toast.error('Please fix the highlighted fields.');
      return;
    }
    setError(null);
    try {
      await create.mutateAsync({
        entityType,
        label: label.trim(),
        dataType,
        required,
        ...(NEEDS_OPTIONS(dataType)
          ? {
              options: options
                .split(',')
                .map((o) => o.trim())
                .filter(Boolean),
            }
          : {}),
      });
      setLabel('');
      setOptions('');
      setRequired(false);
      setFieldErrors({});
      toast.success('Custom field created.');
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const serverFieldErrors = fieldErrorsFromApiError(err);
        if (Object.keys(serverFieldErrors).length > 0) {
          setFieldErrors(serverFieldErrors);
          setError('Please fix the highlighted fields.');
          toast.error('Please fix the highlighted fields.');
          return;
        }
      }
      const message = err instanceof Error ? err.message : 'Could not create the field.';
      setError(message);
      toast.error(message);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 900 }}>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}
      >
        <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Custom fields</h1>
        <span style={{ flex: 1 }} />
        <Button
          variant={entityType === 'CONTACT' ? 'primary' : 'secondary'}
          onClick={() => setEntityType('CONTACT')}
        >
          Contacts
        </Button>
        <Button
          variant={entityType === 'LEAD' ? 'primary' : 'secondary'}
          onClick={() => setEntityType('LEAD')}
        >
          Leads
        </Button>
      </div>

      {canManage ? (
        <Card title={`New ${entityType === 'CONTACT' ? 'contact' : 'lead'} field`}>
          <form
            onSubmit={onCreate}
            style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', alignItems: 'end' }}
          >
            <div style={{ flex: '1 1 180px' }}>
              <TextField
                label="Label"
                value={label}
                maxLength={120}
                onChange={(e) => {
                  setLabel(e.target.value);
                  setFieldErrors((f) => {
                    if (!f.label) return f;
                    const { label: _removed, ...rest } = f;
                    return rest;
                  });
                }}
                error={fieldErrors.label}
              />
            </div>
            <Select
              label="Type"
              value={dataType}
              options={TYPES.map((t) => ({ value: t, label: t }))}
              onChange={(e) => {
                setDataType(e.target.value as CustomFieldDataType);
                setFieldErrors((f) => {
                  if (!f.options) return f;
                  const { options: _removed, ...rest } = f;
                  return rest;
                });
              }}
            />
            {NEEDS_OPTIONS(dataType) ? (
              <div style={{ flex: '1 1 200px' }}>
                <TextField
                  label="Options (comma-separated)"
                  value={options}
                  onChange={(e) => {
                    setOptions(e.target.value);
                    setFieldErrors((f) => {
                      if (!f.options) return f;
                      const { options: _removed, ...rest } = f;
                      return rest;
                    });
                  }}
                  error={fieldErrors.options}
                />
              </div>
            ) : null}
            <label style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={required}
                onChange={(e) => setRequired(e.target.checked)}
              />{' '}
              required
            </label>
            <Button type="submit" disabled={create.isPending || !label.trim()}>
              Create
            </Button>
          </form>
          {error ? (
            <p style={{ margin: 'var(--space-2) 0 0', color: 'var(--color-danger)' }}>{error}</p>
          ) : null}
        </Card>
      ) : null}

      <Card>
        {defs.isPending ? (
          <PageState kind="loading" message="Loading fields…" />
        ) : defs.isError ? (
          <PageState
            kind="error"
            message={defs.error.message}
            onRetry={() => void defs.refetch()}
          />
        ) : defs.data.length === 0 ? (
          <PageState kind="empty" title="No custom fields" message="Define one above." />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="ne-table">
              <thead>
                <tr>
                  <th>Label</th>
                  <th>Key</th>
                  <th>Type</th>
                  <th>Required</th>
                  <th>Options</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {defs.data.map((d) => (
                  <DefRow key={d.id} def={d} canManage={canManage} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/">← Back to dashboard</Link>
      </p>
    </div>
  );
}
